/**
 * Per-streamer actions: take down published images / profile text / gallery,
 * suspend or reinstate the account and lift a force-stop (requirements 3, 6).
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, type User } from '../../lib/api-client';
import { formatJstDateTime } from '../../lib/date-utils';
import { resolveMediaUrl } from '../../lib/media-url';
import type { StreamerContent, StreamerGalleryEntry } from '../../lib/types';
import ReasonDialog, { type ReasonDialogResult } from './ReasonDialog';
import { PROFILE_FIELD_LABELS } from './moderationLabels';

interface StreamerModerationPanelProps {
  isAdmin: boolean;
}

type PendingAction =
  | { kind: 'profileImage' }
  | { kind: 'field'; field: string }
  | { kind: 'gallery'; image: StreamerGalleryEntry }
  | { kind: 'suspend' }
  | { kind: 'liftSuspension' }
  | { kind: 'liftBlock' };

const DIALOG_TEXT: Record<PendingAction['kind'], { title: string; confirmLabel: string; withCategory: boolean; description?: string }> = {
  profileImage: { title: 'プロフィール画像を削除', confirmLabel: '削除する', withCategory: true, description: '公開中の画像を削除し、既定の画像を表示します。' },
  field: { title: 'プロフィール項目を削除', confirmLabel: '削除する', withCategory: true, description: '項目を空にし、翻訳の保存内容も削除します。' },
  gallery: { title: 'ギャラリー画像を削除', confirmLabel: '削除する', withCategory: true },
  suspend: {
    title: 'アカウントを利用停止',
    confirmLabel: '利用停止する',
    withCategory: false,
    description: 'ログイン中のセッションを無効にし、配信中であれば直ちに終了します。解除するまでログインと配信ができず、公開ページにも表示されません。',
  },
  liftSuspension: { title: '利用停止を解除', confirmLabel: '解除する', withCategory: false },
  liftBlock: { title: '配信停止を解除', confirmLabel: '解除する', withCategory: false },
};

export default function StreamerModerationPanel({ isAdmin }: StreamerModerationPanelProps) {
  const [streamers, setStreamers] = useState<User[]>([]);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [content, setContent] = useState<StreamerContent | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      apiClient.listUsers({ role: 'STREAMER', username: search || undefined, limit: 50 })
        .then((response) => setStreamers(response.users))
        .catch((err) => setError(err instanceof Error ? err.message : '配信者一覧を取得できませんでした'));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const loadContent = useCallback(async (streamerId: number) => {
    try {
      setContent(await apiClient.getStreamerContent(streamerId));
      setError(null);
    } catch (err) {
      setContent(null);
      setError(err instanceof Error ? err.message : '配信者の情報を取得できませんでした');
    }
  }, []);

  useEffect(() => {
    if (selectedId !== null) void loadContent(selectedId);
  }, [selectedId, loadContent]);

  const handleConfirm = async (input: ReasonDialogResult) => {
    if (!pending || !content) return;
    const streamerId = content.state.streamer_id;
    switch (pending.kind) {
      case 'profileImage':
        await apiClient.takedownProfileImage(streamerId, input);
        setNotice('プロフィール画像を削除しました。');
        break;
      case 'field':
        await apiClient.takedownProfileField(streamerId, pending.field, input);
        setNotice(`${PROFILE_FIELD_LABELS[pending.field] ?? pending.field}を削除しました。`);
        break;
      case 'gallery':
        await apiClient.takedownGalleryImage(pending.image.id, input);
        setNotice('ギャラリー画像を削除しました。');
        break;
      case 'suspend': {
        const result = await apiClient.suspendStreamer(streamerId, input.reason);
        setNotice(result.terminated_stream_keys.length > 0
          ? 'アカウントを利用停止し、配信中の配信を終了しました。'
          : 'アカウントを利用停止しました。');
        break;
      }
      case 'liftSuspension':
        await apiClient.liftStreamerSuspension(streamerId, input.reason);
        setNotice('利用停止を解除しました。');
        break;
      case 'liftBlock':
        await apiClient.liftPublishBlock(streamerId, input.reason);
        setNotice('配信停止を解除しました。');
        break;
    }
    setPending(null);
    await loadContent(streamerId);
  };

  const state = content?.state;
  const filledFields = content
    ? Object.entries(content.fields).filter(([, value]) => value !== null && value !== '')
    : [];

  return (
    <div className="grid gap-6 md:grid-cols-[16rem_1fr]">
      <aside>
        <h3 className="mb-2 text-lg font-bold">配信者</h3>
        <input
          type="search" value={search} onChange={(event) => setSearch(event.target.value)}
          placeholder="ユーザー名で検索" aria-label="配信者を検索"
          className="mb-2 w-full border border-gray-300 px-2 py-1 text-sm"
        />
        <ul className="max-h-96 divide-y overflow-y-auto border text-sm">
          {streamers.map((streamer) => (
            <li key={streamer.id}>
              <button
                type="button" onClick={() => { setSelectedId(streamer.id); setNotice(null); }}
                className={`w-full px-2 py-2 text-left ${selectedId === streamer.id ? 'bg-pink-100 font-semibold' : 'hover:bg-gray-50'}`}
              >
                {streamer.username}
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="space-y-4">
        {error && <p className="bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</p>}
        {notice && <p className="bg-green-50 p-3 text-sm text-green-800" role="status">{notice}</p>}
        {!content || !state ? (
          <p className="text-sm text-gray-500">左の一覧から配信者を選んでください</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-lg font-bold">{state.streamer_display_name || state.streamer_username}</h3>
              <div className="flex flex-wrap gap-2 text-sm">
                {state.suspended_at ? (
                  <>
                    <span className="bg-gray-800 px-2 py-1 text-white">利用停止中（{formatJstDateTime(state.suspended_at)}）</span>
                    {isAdmin && <button type="button" onClick={() => setPending({ kind: 'liftSuspension' })} className="border border-gray-400 px-2 py-1">利用停止を解除</button>}
                  </>
                ) : (
                  <button type="button" onClick={() => setPending({ kind: 'suspend' })} className="bg-gray-800 px-3 py-1 text-white">アカウントを利用停止</button>
                )}
                {state.publish_blocked_at && (
                  <>
                    <span className="bg-red-700 px-2 py-1 text-white">配信停止中（{formatJstDateTime(state.publish_blocked_at)}）</span>
                    {isAdmin && <button type="button" onClick={() => setPending({ kind: 'liftBlock' })} className="border border-gray-400 px-2 py-1">配信停止を解除</button>}
                  </>
                )}
              </div>
            </div>
            {(state.suspension_reason || state.publish_block_reason) && (
              <p className="text-sm text-gray-600">停止理由: {state.suspension_reason || state.publish_block_reason}</p>
            )}

            <div>
              <h4 className="mb-2 font-semibold">プロフィール画像</h4>
              {content.profile_image_url ? (
                <div className="flex items-end gap-3">
                  <img src={resolveMediaUrl(content.profile_image_url)} alt="公開中のプロフィール画像" className="h-32 w-32 object-cover" />
                  <button type="button" onClick={() => setPending({ kind: 'profileImage' })} className="border border-red-700 px-2 py-1 text-sm text-red-700">削除</button>
                </div>
              ) : (
                <p className="text-sm text-gray-500">公開中の画像はありません</p>
              )}
              {content.pending_profile_image && <p className="mt-1 text-xs text-yellow-700">承認待ちの画像があります（「画像承認」で確認）</p>}
            </div>

            <div>
              <h4 className="mb-2 font-semibold">プロフィール項目</h4>
              {filledFields.length === 0 ? (
                <p className="text-sm text-gray-500">入力されている項目はありません</p>
              ) : (
                <dl className="divide-y border text-sm">
                  {filledFields.map(([field, value]) => (
                    <div key={field} className="flex items-start justify-between gap-2 p-2">
                      <div><dt className="font-semibold">{PROFILE_FIELD_LABELS[field] ?? field}</dt><dd className="whitespace-pre-wrap text-gray-700">{value}</dd></div>
                      <button type="button" onClick={() => setPending({ kind: 'field', field })} className="shrink-0 border border-red-700 px-2 py-1 text-red-700">削除</button>
                    </div>
                  ))}
                </dl>
              )}
            </div>

            <div>
              <h4 className="mb-2 font-semibold">ギャラリー</h4>
              {content.gallery.length === 0 ? (
                <p className="text-sm text-gray-500">ギャラリー画像はありません</p>
              ) : (
                <ul className="flex flex-wrap gap-3">
                  {content.gallery.map((image) => (
                    <li key={image.id} className="w-32 text-xs">
                      <img src={resolveMediaUrl(image.image_url)} alt={`ギャラリー ${image.display_order}`} className="h-32 w-32 object-cover" />
                      <div className="mt-1 flex items-center justify-between">
                        <span>{image.status === 'approved' ? '公開中' : '承認待ち'}</span>
                        <button type="button" onClick={() => setPending({ kind: 'gallery', image })} className="border border-red-700 px-1 text-red-700">削除</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </section>

      {pending && (
        <ReasonDialog
          open
          {...DIALOG_TEXT[pending.kind]}
          onCancel={() => setPending(null)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}
